import * as commsApi from '../../api/comms';
import { type DispatchClient, makeDispatchClient } from '../../api/dispatch';
import type { HostJob, HostProfile } from '../../embed/bridge';
import type { DialerHost } from '../../embed/dialerState';
import type { PhoneHost } from '../../embed/phoneState';
import { config } from '../../config';
import { subscribeRealtime, type CommsRealtimeEvent } from '../../realtime/socket';

/**
 * Everything the Communications UI needs from the outside world, behind one
 * seam. The real source wires the CRM API + socket; the dev-only preview route
 * substitutes fixtures so the module renders without a session (and the same
 * seam is where a Flutter-embedded token bridge slots in later).
 */

export type CommsApi = typeof commsApi;

export interface CommsSource {
  api: CommsApi;
  /**
   * The tenant this module is looking at — the name, as the socket and the
   * `Tenant` header use it. Here rather than read from the auth context
   * because the embed has no local session to read it from, and anything
   * this browser files per tenant (composer drafts) would otherwise be filed
   * under a blank one and shared with the next tenant's conversation 42.
   */
  tenant: string;
  /**
   * Direct browser → dispatch-be calls for the booking flow (places, vehicle
   * types, job/draft create). Required so the preview fixtures are forced to
   * implement it — the same forcing function as `CommsApi`.
   */
  dispatch: DispatchClient;
  /** Subscribe to tenant realtime events; returns the unsubscribe. */
  subscribe: (handler: (event: CommsRealtimeEvent) => void) => () => void;
  /** The hosted RingCentral Embeddable page, or null to hide the dialer. */
  dialerUrl: string | null;
  /**
   * True inside the dashboard iframe. Channel-configuration actions then open
   * the standalone console in a NEW browser tab instead of navigating — OAuth
   * consent pages refuse to render inside frames.
   */
  embedded: boolean;
  /**
   * Framed by a dashboard that has its own website pages (`websiteHost` on
   * INIT), so the module does not offer Analytics as one of its tabs.
   */
  websiteHost?: boolean;
  /** Embed only: relay an arriving notification to the parent dashboard. */
  notifyHost?: (payload: unknown) => void;
  /**
   * Embed only: tell the parent dashboard these notifications were just marked
   * read here — opening a conversation reads its notifications — so the count
   * on its own bell drops with them instead of at its next re-read.
   */
  notifyHostRead?: (ids: number[]) => void;
  /** Embed only: relay live call events (ringing/answered/ended) to the dashboard. */
  notifyCallHost?: (event: string, payload: unknown) => void;
  /**
   * Embed only, and only where the parent said it handles OPEN_PROFILE: ask the
   * dashboard to open one of its own records. Absent everywhere else, which is
   * what the contact panel reads to decide between a button, a link and the
   * bare id — see ProfileLink.
   */
  openProfile?: (profile: HostProfile) => void;
  /**
   * Embed only, and only where the parent said it handles OPEN_JOB: ask the
   * dashboard to open a booking. Absent everywhere else, which is what a job
   * number reads to decide between a button, a link and plain text — see
   * booking/JobLink.tsx.
   */
  openJob?: (job: HostJob) => void;
  /**
   * The Twilio softphone, when the host holds it rather than this module.
   *
   * Non-null only inside a dashboard new enough to mount the `/phone` frame
   * itself. Everywhere else it is null and the module registers its own device
   * — never both, or one Twilio identity would carry two registrations that
   * ring together and race to answer (see useHostPhone).
   */
  phoneHost: PhoneHost | null;
  /**
   * The RingCentral dialer, when the host holds it rather than this module.
   *
   * Non-null only inside a dashboard new enough to keep the `/dialer` frame in
   * its own shell. Everywhere else it is null and the module mounts its own
   * (`dialerUrl` permitting) — never both, or one RingCentral user would carry
   * two web phones that fork every ring (see useHostDialer).
   */
  dialerHost: DialerHost | null;
}

export function makeRealSource(tenant: string): CommsSource {
  return {
    api: commsApi,
    tenant,
    dispatch: makeDispatchClient(),
    subscribe: (handler) => subscribeRealtime(tenant, handler),
    dialerUrl: `${config.crmBase}/dialer`,
    embedded: false,
    phoneHost: null,
    dialerHost: null,
  };
}

/** The /embed iframe: same API, but tokens come from the parent's bridge. */
export function makeEmbeddedSource(
  tenant: string,
  getToken: (forceFresh: boolean) => Promise<string>,
  notifyHost?: (payload: unknown) => void,
  notifyCallHost?: (event: string, payload: unknown) => void,
  phoneHost: PhoneHost | null = null,
  dialerHost: DialerHost | null = null,
  openProfile?: (profile: HostProfile) => void,
  openJob?: (job: HostJob) => void,
  notifyHostRead?: (ids: number[]) => void,
): CommsSource {
  return {
    api: commsApi,
    tenant,
    // makeDispatchClient reads tokens through getAuthToken, which already
    // prefers the embedded session — no separate embed wiring needed.
    dispatch: makeDispatchClient(),
    subscribe: (handler) => subscribeRealtime(tenant, handler, getToken),
    dialerUrl: `${config.crmBase}/dialer`,
    embedded: true,
    notifyHost,
    notifyHostRead,
    notifyCallHost,
    phoneHost,
    dialerHost,
    openProfile,
    openJob,
  };
}
